import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { useToast } from '@/legacy/hooks/useToast';
import { fetchRequest } from '@/legacy/services';
import { useTranslation } from 'react-i18next';
import { readUnifiedError, resolveApiError } from '@/shared/api-error';
import { resolveBulkError, resolveBulkSuccess } from '@/shared/api-error/bulkOperation';
import { RequestMethod } from '@/legacy/types';
import { successMessageTradPrefix, errorMessageTradPrefix } from '@/legacy/i18n';
import { AxiosResponse } from 'axios';
import { ACTION_HEADER_KEY } from '@/app/analytics/actionsIds';

export type BaseRequest = {
	method?: RequestMethod;
	endpoint?: string | ((record: any) => string);
	payload?: any;
	responseType?: 'json' | 'blob' | 'text';
	actionId?: string; // Id to identify the action in analytics and tests
};

type GenericMutationOptions<TVariables extends BaseRequest, TBody = any, TError = any> = {
	onSuccess?: (response: AxiosResponse<TBody>, variables: TVariables) => void;
	onSettled?: () => void;
	onError?: (error: TError) => void;
	successMessage?: string | ((vars: TVariables) => string);
	errorMessage?: string;
	invalidateKeys?: (string | readonly unknown[])[];
	dontTranslateSuccessMessage?: boolean;
	muteErrorToastWhen?: (error: any, ctx: { status?: number; message?: string }) => boolean;
};

export const useGenericMutation = <
	TVariables extends BaseRequest = BaseRequest,
	TBody = any,
	TError = unknown,
>({
	onSuccess,
	onSettled = () => {},
	onError,
	successMessage = '',
	errorMessage = '',
	invalidateKeys = [],
	dontTranslateSuccessMessage = false,
	muteErrorToastWhen = () => false,
}: GenericMutationOptions<TVariables, TBody, TError> = {}): UseMutationResult<
	AxiosResponse<TBody>,
	TError,
	TVariables
> => {
	const { t } = useTranslation();
	const { showToast } = useToast();
	const queryClient = useQueryClient();

	const resolveSuccessMessage = (vars: TVariables) => {
		const raw = typeof successMessage === 'function' ? successMessage(vars) : successMessage;
		if (!raw) return `${successMessageTradPrefix}.action_successfull`;
		return dontTranslateSuccessMessage
			? raw
			: raw.includes('.')
				? raw
				: `${successMessageTradPrefix}.${raw}`;
	};

	return useMutation<AxiosResponse<TBody>, TError, TVariables>({
		mutationFn: async (vars: TVariables): Promise<AxiosResponse<TBody>> => {
			const { method = 'GET', endpoint, payload = {}, responseType = 'json' } = vars;
			const resolvedEndpoint = typeof endpoint === 'function' ? endpoint({}) : (endpoint ?? '');

			const res = await fetchRequest({
				url: resolvedEndpoint,
				method,
				data: payload,
				responseType,
				headers: {
					[ACTION_HEADER_KEY]: vars.actionId || '',
				},
			});

			return res as AxiosResponse<TBody>;
		},
		onSuccess: (response, variables) => {
			const bulk = resolveBulkSuccess(response.data);

			if (bulk) {
				showToast(bulk.message.text, bulk.tone);
			} else {
				showToast(t(resolveSuccessMessage(variables)), 'success');
			}

			onSuccess?.(response, variables);

			if (invalidateKeys.length) {
				invalidateKeys.forEach((key) =>
					queryClient.removeQueries({ queryKey: Array.isArray(key) ? key : [key] }),
				);
			}

			onSettled();
		},
		onError: (error: any) => {
			// Reuse the interceptor's single resolution when available (already deduped via
			// `__unifiedError`); only re-resolve when this error never passed through it.
			const resolved = readUnifiedError(error)?.resolved ?? resolveApiError(error);
			const bulk = resolveBulkError(resolved);

			if (bulk) {
				showToast(bulk.message.text, bulk.tone);
				return;
			}

			const status = error?.response?.status;
			// `errorMessage` is a caller-supplied translation KEY -> needs t(). `resolved.message`
			// is already display-ready text from the unified module -> must NOT be re-translated.
			// `errorMessage` is a runtime `string`, not a literal member of the generated `errors`
			// namespace key union, so it can't satisfy `TFunction`'s typed overload for a
			// namespaced call — same permanent, structural reasoning as `contract.ts`.
			const text = errorMessage
				? String(t(errorMessage, { ns: errorMessageTradPrefix } as never))
				: resolved.message.text;

			if (muteErrorToastWhen(error, { status, message: text })) {
				onError?.(error as TError);
				return;
			}

			showToast(text, 'error');
			onError?.(error as TError);
		},
	});
};
